const SAMPLE_SIZE = 64;
const TEXTURE_SIZE = SAMPLE_SIZE * 3;
const CORNERS = [[0, 0], [1, 0], [1, 1], [0, 1]];
const backgrounds = new WeakMap();
let sharedSampleCanvas = null;

function fallbackChannels(color) {
  const hex = /^#([\da-f]{6}|[\da-f]{3})$/i.exec(color || '');
  if (!hex) return [31, 31, 31];
  const value = hex[1].length === 3
    ? hex[1].split('').map(channel => channel + channel).join('')
    : hex[1];
  return [0, 2, 4].map(offset => parseInt(value.slice(offset, offset + 2), 16));
}

function rgb(channels) {
  return `rgb(${channels.map(Math.round).join(', ')})`;
}

function samplePerimeter(source, fallback) {
  const sample = sharedSampleCanvas || (sharedSampleCanvas = document.createElement('canvas'));
  sample.width = sample.height = SAMPLE_SIZE;
  const context = sample.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  const last = SAMPLE_SIZE - 1;
  const { width, height } = source;
  // Read the actual outside pixels, rather than a downsampled band of the
  // image's interior. Shared corners keep neighboring edge colors continuous.
  context.drawImage(source, 0, 0, width, 1, 0, 0, SAMPLE_SIZE, 1);
  context.drawImage(source, width - 1, 0, 1, height, last, 0, 1, SAMPLE_SIZE);
  context.drawImage(source, 0, height - 1, width, 1, 0, last, SAMPLE_SIZE, 1);
  context.drawImage(source, 0, 0, 1, height, 0, 0, 1, SAMPLE_SIZE);
  for (const [x, y] of CORNERS) {
    context.clearRect(x * last, y * last, 1, 1);
    context.drawImage(source, x * (width - 1), y * (height - 1), 1, 1,
      x * last, y * last, 1, 1);
  }
  const pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
  sample.width = 0;
  sample.height = 0;
  const perimeter = new Array(last * 4);
  let index = 0;
  for (let side = 0; side < 4; side++) {
    for (let point = 0; point < last; point++) {
      const x = side === 0 ? point : side === 1 ? last : side === 2 ? last - point : 0;
      const y = side === 0 ? 0 : side === 1 ? point : side === 2 ? last : last - point;
      const offset = (y * SAMPLE_SIZE + x) * 4;
      const alpha = pixels[offset + 3] / 255;
      const invAlpha = 1 - alpha;
      perimeter[index++] = [
        pixels[offset] * alpha + fallback[0] * invAlpha,
        pixels[offset + 1] * alpha + fallback[1] * invAlpha,
        pixels[offset + 2] * alpha + fallback[2] * invAlpha,
      ];
    }
  }
  return perimeter;
}

function makeTexture(perimeter, mean) {
  const texture = document.createElement('canvas');
  texture.width = texture.height = TEXTURE_SIZE;
  const context = texture.getContext('2d');
  if (!context) return null;
  const image = context.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
  const data = image.data;
  const side = SAMPLE_SIZE - 1;
  const perimeterLength = perimeter.length;
  const topLeft = perimeter[0];
  const topRight = perimeter[side];
  const bottomRight = perimeter[side * 2];
  const bottomLeft = perimeter[side * 3];
  const sampleAt = (position, out) => {
    const wrapped = ((position % perimeterLength) + perimeterLength) % perimeterLength;
    const index = Math.floor(wrapped);
    const weight = wrapped - index;
    const invWeight = 1 - weight;
    const start = perimeter[index];
    const end = perimeter[(index + 1) % perimeterLength];
    out[0] = start[0] * invWeight + end[0] * weight;
    out[1] = start[1] * invWeight + end[1] * weight;
    out[2] = start[2] * invWeight + end[2] * weight;
  };
  const topSample = [0, 0, 0];
  const bottomSample = [0, 0, 0];
  const leftSample = [0, 0, 0];
  const rightSample = [0, 0, 0];
  const s0 = [0, 0, 0];
  const s1 = [0, 0, 0];
  const s2 = [0, 0, 0];
  const s3 = [0, 0, 0];
  const s4 = [0, 0, 0];
  for (let y = 0; y < TEXTURE_SIZE; y++) {
    const v = (y + 0.5 - SAMPLE_SIZE) / SAMPLE_SIZE;
    const cy = Math.max(0, Math.min(1, v));
    const dy = Math.abs(v - cy);
    const invCy = 1 - cy;
    if (dy === 0) {
      sampleAt((4 - cy) * side, leftSample);
      sampleAt((1 + cy) * side, rightSample);
    }
    for (let x = 0; x < TEXTURE_SIZE; x++) {
      const u = (x + 0.5 - SAMPLE_SIZE) / SAMPLE_SIZE;
      const cx = Math.max(0, Math.min(1, u));
      const dx = Math.abs(u - cx);
      const distance = Math.max(dx, dy);
      const fade = Math.min(1, distance * SAMPLE_SIZE / (SAMPLE_SIZE - 0.5));
      const blend = fade * fade * (3 - 2 * fade);
      const invBlend = 1 - blend;
      const offset = (y * TEXTURE_SIZE + x) * 4;
      if (distance === 0) {
        // A continuous interpolation also covers the boundary pixels beneath
        // a subpixel snapshot; fully interior pixels are clipped out later.
        const invCx = 1 - cx;
        sampleAt(cx * side, topSample);
        sampleAt((3 - cx) * side, bottomSample);
        for (let channel = 0; channel < 3; channel++) {
          const corners = topLeft[channel] * invCx * invCy
            + topRight[channel] * cx * invCy
            + bottomRight[channel] * cx * cy
            + bottomLeft[channel] * invCx * cy;
          const color = topSample[channel] * invCy
            + bottomSample[channel] * cy
            + leftSample[channel] * invCx
            + rightSample[channel] * cx - corners;
          data[offset + channel] = color * invBlend + mean[channel] * blend;
        }
      } else {
        // Blur around the whole perimeter, including corners. Increasing the
        // radius outward avoids long streaks from individual edge details.
        const position = v <= 0 ? cx * side
          : u >= 1 ? (1 + cy) * side
            : v >= 1 ? (3 - cx) * side : (4 - cy) * side;
        const radius = Math.hypot(dx, dy) * side / 3;
        const halfRadius = radius / 2;
        sampleAt(position - radius, s0);
        sampleAt(position - halfRadius, s1);
        sampleAt(position, s2);
        sampleAt(position + halfRadius, s3);
        sampleAt(position + radius, s4);
        for (let channel = 0; channel < 3; channel++) {
          const color = (s0[channel]
            + 4 * s1[channel]
            + 6 * s2[channel]
            + 4 * s3[channel]
            + s4[channel]) / 16;
          data[offset + channel] = color * invBlend + mean[channel] * blend;
        }
      }
      data[offset + 3] = 255;
    }
  }
  context.putImageData(image, 0, 0);
  return texture;
}

// Snapshot canvases are immutable. Build the tiny surrounding texture once,
// keeping pixel readback and color estimation out of the animation frames.
function previewBackground(sourceCanvas, fallbackColor = '#1f1f1f') {
  const canCache = Boolean(sourceCanvas && typeof sourceCanvas === 'object');
  let cached = canCache ? backgrounds.get(sourceCanvas) : undefined;
  if (cached?.has(fallbackColor)) return cached.get(fallbackColor);
  const fallback = fallbackChannels(fallbackColor);
  const background = { color: rgb(fallback), texture: null };
  if (!canCache) return background;
  if (!cached) {
    cached = new Map();
    backgrounds.set(sourceCanvas, cached);
  }
  if (sourceCanvas.width > 0 && sourceCanvas.height > 0) {
    try {
      const perimeter = samplePerimeter(sourceCanvas, fallback);
      if (perimeter) {
        const mean = [0, 0, 0];
        for (let i = 0; i < perimeter.length; i++) {
          mean[0] += perimeter[i][0];
          mean[1] += perimeter[i][1];
          mean[2] += perimeter[i][2];
        }
        mean[0] /= perimeter.length;
        mean[1] /= perimeter.length;
        mean[2] /= perimeter.length;
        background.color = rgb(mean);
        background.texture = makeTexture(perimeter, mean);
      }
    } catch {
      // Missing canvas support or unavailable readback must not interrupt
      // navigation. A flat fill still uses the same seamless coverage mask.
    }
  }
  cached.set(fallbackColor, background);
  return background;
}

export function estimatePreviewBackground(sourceCanvas, fallbackColor) {
  return previewBackground(sourceCanvas, fallbackColor).color;
}

export function releasePreviewBackground(sourceCanvas) {
  if (!sourceCanvas || typeof sourceCanvas !== 'object') return;
  const cached = backgrounds.get(sourceCanvas);
  if (!cached) return;
  backgrounds.delete(sourceCanvas);
  for (const entry of cached.values()) {
    if (entry?.texture && entry.texture !== sourceCanvas) {
      entry.texture.width = 0;
      entry.texture.height = 0;
      entry.texture = null;
    }
  }
}

// One integer-edged mask preserves fully interior snapshot pixels while
// covering its fractional boundary pixels. Separate antialiased rectangles
// leave cracks where their partial coverage meets during smooth zooming.
export function fillExposedBackground(context, width, height, bounds, sourceCanvas, fallbackColor, feather) {
  if (!(width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height))) return;
  const valid = Boolean(
    bounds
    && Number.isFinite(bounds.left)
    && Number.isFinite(bounds.top)
    && Number.isFinite(bounds.right)
    && Number.isFinite(bounds.bottom)
  );
  if (valid && bounds.left <= 0 && bounds.top <= 0 && bounds.right >= width && bounds.bottom >= height) return;
  const background = previewBackground(sourceCanvas, fallbackColor);
  context.save();
  try {
    context.beginPath();
    context.rect(0, 0, width, height);
    if (valid) {
      const featherX = feather?.x || 0;
      const featherY = feather?.y || 0;
      const left = Math.max(0, Math.ceil(bounds.left + (bounds.left > 0 ? featherX : 0)));
      const top = Math.max(0, Math.ceil(bounds.top + (bounds.top > 0 ? featherY : 0)));
      const right = Math.min(width, Math.floor(bounds.right - (bounds.right < width ? featherX : 0)));
      const bottom = Math.min(height, Math.floor(bounds.bottom - (bounds.bottom < height ? featherY : 0)));
      if (right > left && bottom > top) context.rect(left, top, right - left, bottom - top);
    }
    context.clip('evenodd');
    context.fillStyle = background.color;
    context.fillRect(0, 0, width, height);
    const spanX = valid ? bounds.right - bounds.left : 0;
    const spanY = valid ? bounds.bottom - bounds.top : 0;
    if (background.texture && spanX > 0 && spanY > 0 && Number.isFinite(spanX) && Number.isFinite(spanY)) {
      const x = Math.max(0, bounds.left - spanX);
      const y = Math.max(0, bounds.top - spanY);
      const right = Math.min(width, bounds.right + spanX);
      const bottom = Math.min(height, bounds.bottom + spanY);
      if (right > x && bottom > y) {
        context.imageSmoothingEnabled = true;
        // Crop before drawing, so a highly magnified or offscreen snapshot
        // never requests a huge destination rectangle from Canvas2D.
        context.drawImage(background.texture,
          SAMPLE_SIZE + (x - bounds.left) / spanX * SAMPLE_SIZE,
          SAMPLE_SIZE + (y - bounds.top) / spanY * SAMPLE_SIZE,
          (right - x) / spanX * SAMPLE_SIZE, (bottom - y) / spanY * SAMPLE_SIZE,
          x, y, right - x, bottom - y);
      }
    }
  } finally {
    context.restore();
  }
  return background.color;
}
